import { useEffect, useState, useRef } from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Alert, AlertDescription } from "../ui/alert";
import { ArrowLeft, Mail, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../ui/input-otp";
import productionBackground from "../assets/FlippedKarbala.svg";
import stagingBackground from "../assets/fhimsWallpaper.jpeg";
import logoImage from "../assets/logo.png";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../services/axiosInstance";
import { useAuth } from "../contexts/AuthContext";
import { maskLoginId } from "../utils/commonutils"; 

interface VerifyOtpPageProps {
  email?: string;
}

export const VerifyOtpPage = ({ email }: VerifyOtpPageProps) => {
  const navigate = useNavigate();

  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const { verifyOtp, setDynamicMenuListFun } = useAuth();

  const OTP_DURATION = 5 * 60;

  const [otpTimer, setOtpTimer] = useState(OTP_DURATION);
  const [isOtpExpired, setIsOtpExpired] = useState(false);

  const loginId = localStorage.getItem("fhims_loginId") || "";
  const maskedLoginId = maskLoginId(loginId);

  const isEmailLogin = loginId.includes("@");

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const startOtpTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setIsOtpExpired(false);
    setOtpTimer(OTP_DURATION);

    timerRef.current = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          setIsOtpExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    startOtpTimer();

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
    

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (otp.length !== 6) {
      setError("Please enter a valid 6-digit OTP");
      return;
    }

    const loginId = localStorage.getItem("fhims_loginId");

    if (!loginId) {
      setError("Session expired. Please login again.");
      navigate("/");
      return;
    }

    setIsLoading(true);

    try {
      await verifyOtp(otp);

      const menuRes = await axiosInstance.get("/Auth/me/menu");
      setDynamicMenuListFun(menuRes?.data);
      localStorage.setItem("fhims_menu", JSON.stringify(menuRes.data));

      toast.success("OTP verified successfully");
      navigate("/dashboard");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (err.response?.status === 400 || err.response?.status === 401) {
        setError("Invalid OTP. Please try again.");
      } else {
        setError("Verification failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    const loginId = localStorage.getItem("fhims_loginId");

    if (!loginId) {
      toast.error("Session expired. Please login again.");
      navigate("/");
      return;
    }

    try {
      setIsLoading(true);
      setOtp("");

      const response = await axiosInstance.post("/Auth/login/resend-otp", {
        loginId,
      });

      if (response.status === 200 && response.data) {
        toast.success(response.data.message || "OTP resent successfully");
        startOtpTimer();  
      } else {
        toast.error("Failed to resend OTP");
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      toast.error("Server error while resending OTP.");
    } finally {
      setIsLoading(false);
    }
  };

  const backgroundImage =
    process.env.REACT_APP_ENVIRONMENT === "staging"
      ? stagingBackground
      : productionBackground;

  return (
    <div className="h-screen w-full relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src={backgroundImage}
          alt="Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30"></div>
      </div>

      {/* Content */}
      <div className="relative h-full flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <img
              src={logoImage}
              alt="Logo"
              className="w-32 h-32 object-contain drop-shadow-2xl"
            />
          </div>

          {/* Heading */}
          <div className="text-center mb-5">
            <h1 className="text-white text-2xl mb-1.5 drop-shadow-lg">
              Verify OTP
            </h1>
            <p className="text-white/90 text-sm drop-shadow-lg">
              Enter the code sent to your email
            </p>
          </div>

          {/* Card */}
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-6">
            <form onSubmit={handleVerifyOTP} className="space-y-3">
              {/* OTP Info */}
              <div className="text-center bg-blue-50 rounded-xl p-3 border border-blue-200">
                <div className="flex items-center justify-center gap-2 mb-1.5">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <h3 className="text-gray-900 text-sm">OTP Sent</h3>
                </div>
                <p className="text-xs text-gray-600">
                  Check your email for the 6-digit OTP
                  <br />
                  {/* <span className="text-blue-600">{maskedLoginId}</span> */}
                </p>
              </div>

              {/* Error */}
              {error && (
                <Alert
                  variant="destructive"
                  className="border-red-200 bg-red-50 py-2.5"
                >
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription className="text-red-800 text-xs">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              {/* OTP Input */}
              <div className="space-y-2">
                <Label
                  htmlFor="otp"
                  className="text-gray-700 text-xs text-center block"
                >
                  Enter OTP <span className="text-red-500">*</span>
                </Label>

                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    disabled={isOtpExpired}
                    onChange={(value) => setOtp(value)}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot
                        index={0}
                        className="w-11 h-11 text-lg border-gray-300"
                      />
                      <InputOTPSlot
                        index={1}
                        className="w-11 h-11 text-lg border-gray-300"
                      />
                      <InputOTPSlot
                        index={2}
                        className="w-11 h-11 text-lg border-gray-300"
                      />
                      <InputOTPSlot
                        index={3}
                        className="w-11 h-11 text-lg border-gray-300"
                      />
                      <InputOTPSlot
                        index={4}
                        className="w-11 h-11 text-lg border-gray-300"
                      />
                      <InputOTPSlot
                        index={5}
                        className="w-11 h-11 text-lg border-gray-300"
                      />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                {isEmailLogin && (
                  <p className="text-xs text-gray-500 text-center">
                    OTP sent to {maskedLoginId}
                  </p>
                )}
              </div>



              {/* Verify Button */}
              <Button
                type="submit"
                className="w-full h-9 text-white shadow-lg hover:opacity-90 transition-opacity text-sm"
                style={{ backgroundColor: "#5B9BD5" }}
                disabled={isLoading || otp.length !== 6 || isOtpExpired}
              >
                {isLoading ? "Verifying..." : "Verify & Continue"}
              </Button>

              <p className="text-xs sm:text-sm text-center">
                {!isOtpExpired ? (
                  <>
                    OTP expires in{" "}
                    <span
                      className={`font-medium ${
                        otpTimer <= 30 ? "text-red-500" : "text-blue-600"
                      }`}
                    >
                      {formatTime(otpTimer)}
                    </span>
                  </>
                ) : (
                  <span className="text-gray-500 font-medium">
                    OTP has expired
                  </span>
                )}
              </p>

              {/* Actions */}
              <div className="text-center space-y-1.5 pt-1">
                {isOtpExpired && (
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={isLoading}
                    className="text-sm text-blue-600 hover:text-blue-700 block w-full disabled:opacity-50"
                  >
                    Resend OTP
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => {
                    localStorage.removeItem("fhims_loginId");
                    localStorage.removeItem("fhims_auth_user");
                    localStorage.removeItem("fhims_auth_token");
                    localStorage.removeItem("fhims_menu");
                    navigate("/", { replace: true });
                  }}
                  className="text-sm text-gray-600 hover:text-gray-800 inline-flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </button>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="text-center mt-3 text-white/90 text-[10px] drop-shadow-lg">
            <p>© 2025 Faiz e Husaini Information Management System</p>
          </div>
        </div>
      </div>
    </div>
  );
};
